/**
 * usePermissions — fetches /api/access-requests/me on mount and exposes
 * the user's Tier-B state ({role, departments, grants, requests}) plus
 * a stable `canAccess(button)` helper.
 *
 * iter-119 — also fetches /api/button-toggles/public (no-auth) so the
 * admin/engineer global per-button kill-switch flows through the same
 * gate. Behaviour:
 *   • disabled + non-admin → canAccess = false (button hidden as if
 *     they had no department access).
 *   • disabled + admin-like → canAccess still true (they keep clicking
 *     to manage/re-enable) AND `isDisabledByAdmin(button)` is true so
 *     the JSX can apply a greyed-out "Disabled" overlay.
 *
 * Returns:
 *   { loading, role, departments, grants, requests,
 *     disabledButtons, canAccess, isDisabledByAdmin, refresh, token }
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { hasButtonAccess, isAdminLike } from './permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function readToken() {
  return typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
}

export default function usePermissions() {
  const [token, setToken] = useState(readToken());
  const [state, setState] = useState({
    loading: !!token,
    role: 'viewer',
    departments: [],
    grants: [],
    requests: [],
    disabledButtons: [],
  });

  const fetchDisabledButtons = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/button-toggles/public`);
      return (data?.disabled || []).map((r) => r.button_id);
    } catch {
      return [];
    }
  }, []);

  const refresh = useCallback(async () => {
    const t = readToken();
    setToken(t);
    // Always fetch the disabled-button list (no-auth endpoint).
    const disabledButtons = await fetchDisabledButtons();
    if (!t) {
      setState({ loading: false, role: 'viewer', departments: [], grants: [], requests: [], disabledButtons });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await axios.get(`${API}/access-requests/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setState({
        loading: false,
        role: data?.role || 'viewer',
        departments: data?.departments || [],
        grants: data?.grants || [],
        requests: data?.requests || [],
        disabledButtons,
      });
    } catch {
      setState((s) => ({ ...s, loading: false, disabledButtons }));
    }
  }, [fetchDisabledButtons]);

  // Initial fetch.
  useEffect(() => { refresh(); }, [refresh]);

  // React to sign-in/out in other tabs (storage event) AND in the same
  // tab via a custom `atrium:auth-changed` event dispatched on login/out.
  useEffect(() => {
    const onStorage = (e) => { if (!e || e.key === 'admin_token') refresh(); };
    const onAuthChanged = () => refresh();
    const onTogglesChanged = () => refresh();
    window.addEventListener('storage', onStorage);
    window.addEventListener('atrium:auth-changed', onAuthChanged);
    window.addEventListener('atrium:button-toggles-changed', onTogglesChanged);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('atrium:auth-changed', onAuthChanged);
      window.removeEventListener('atrium:button-toggles-changed', onTogglesChanged);
    };
  }, [refresh]);

  const isDisabledByAdmin = useCallback(
    (button) => state.disabledButtons.includes(button),
    [state.disabledButtons],
  );

  const canAccess = useCallback(
    (button) => {
      // While loading, treat everything as accessible to avoid a flash of
      // padlocks before the /me response arrives.
      if (state.loading) return true;
      // Global admin kill-switch: hide for non-admin-likes, keep visible
      // for admins/engineers (their UI shows a greyed-out Disabled badge).
      if (state.disabledButtons.includes(button) && !isAdminLike(state.role)) {
        return false;
      }
      return hasButtonAccess(state.role, state.departments, state.grants, button);
    },
    [state.loading, state.role, state.departments, state.grants, state.disabledButtons],
  );

  return { ...state, canAccess, isDisabledByAdmin, refresh, token };
}
